import {
    Routes,
    Route,
    Navigate,
    useLocation,
    matchPath,
} from 'react-router-dom';
import { useAuthContext } from './store/Contexts';
import { SecurityAttributes, UserType } from './utils/data/enums';
import MultiTenantHomePage from './components/home/MultiTenantHomePage';
import ClientAdminHomePage from './components/home/ClientAdminHomePage';
import IndividualHomePage from './components/home/IndividualHomePage';
import AboutPage from './components/AboutPage';
import LoginPage from './components/auth/AuthPage';
import NotFoundPage from './components/NotFoundPage';
import PopulationGroupsPage from './components/population-groups/PopulationGroupsPage';
import BrandingPage from './components/branding/BrandingPage';
import ProfileComponentPassword from './components/branding/ProfileComponentPassword';
import ProfileComponentContactUs from './components/branding/ProfileComponentContactUs';
import ProfileComponentPrivacyPolicy from './components/branding/ProfileComponentPrivacyPolicy';
// import ProfileComponentFAQ from './components/branding/ProfileComponentFAQ';
import ProfileComponent from './components/branding/ProfileComponent';
import ProfileComponentUserInfo from './components/branding/ProfileComponentUserInfo';
import OnboardingPage from './components/onboarding/OnboardingPage';
import PageList from './components/page-list/PageList';
import Charts from './components/charts/Charts';
import NewPortalPage from './components/new-portal-page/NewPortalPage';
import NewFileUploadPage from './components/fileImports/NewFileUploadPage';
import ImportAutomationListPage from './components/fileImports/ImportAutomationListPage';
import EditAutoImport from './components/fileImports/EditAutoImport';
import ImportQueuePage from './components/fileImports/ImportQueuePage';
// import TemplateList from './components/templates/TemplateList';
// import TemplatePage from './components/templates/TemplatePage';
import VerifyMFAPage from './components/auth/VerifyMFA';
import ListSecurityRoles from './components/security-roles/ListSecurityRoles';
import ListUsersForRole from './components/security-roles/ListUsersForRole';
import AddUserToRole from './components/security-roles/AddUserToRole';
import AddSecurityRole from './components/security-roles/AddSecurityRole';
import IndividualSearchPage from './components/individual/IndividualSearchPage';
import IndividualTabsPage from './components/individual/IndividualTabsPage';
import OrgLevelsList from './components/orgLevels/OrgLevelsList';
import EditOrgLevel from './components/orgLevels/Edit';
import DocumentViewer from './components/document-component/DocumentViewer';
import CreateIndividualPage from './components/individual/CreateIndividualPage';
import ThemePage from './components/theme/ThemePage';
import ImportTemplatesPage from './components/customImports/ImportTemplatesPage';
import AddClientTabsPage from './components/clients/AddClientTabsPage';
import PortalPageView from './components/child-portal-component/PortalPageView';
import UserAccountList from './components/user-account/UserAccountList';
import UserAccountView from './components/user-account/UserAccountView';
import AdditionalUsersList from './components/additional-users/AdditionalUsersList';
import AdditionalUserForm from './components/additional-users/AdditionalUserForm';
import ClientList from './components/clients/ClientList';
import ClientView from './components/clients/ClientView';
import AddContact from './components/clients/AddContact';
import AssignUser from './components/clients/AssignUser';
import MultiTenantSSOWizard from './components/sso/MultiTenantSSOWizard';
import SSOList from './components/sso/SSOList';
import ClientSSOWizard from './components/sso/ClientSSOWizard';
import SSOLinkRedirect from './components/sso/SSOLinkRedirect';
import AssetLibrary from './components/assets/AssetLibrary';
import NewAsset from './components/assets/NewAsset';
import OnboardingWizard from './components/onboarding/OnboardingWizard';
import ForgotEmailVerification from './components/auth/recovery/ForgotEmailVerification';
import GetSupport from './components/auth/recovery/GetSupport';
import PortalActionsList from './components/portal-actions/PortalActionsList';
import PortalActionDetail from './components/portal-actions/PortalActionDetail';
import MessageList from './components/notifications/MessageList';
import MessagesHistory from './components/notifications/MessagesHistory';
import MessagesHistoryDetails from './components/notifications/MessagesHistoryDetails';
import AddMessageTabsPage from './components/notifications/AddMessageTabsPage';
import NotificationScheduleList from './components/notifications/NotificationScheduleList';
import NotificationScheduleDetails from './components/notifications/NotificationScheduleDetails';
import NotificationScheduleHistory from './components/notifications/NotificationScheduleHistory';
import MyAssets from './components/assets/MyAssets';
import UserMessageList from './components/notifications/userMessages/UserMessageList';
import AgencyList from './components/agencies/AgencyList';
import AgencyTabsPage from './components/agencies/AgencyTabsPage';
import ProfileComponentInstallationInstructions from './components/branding/ProfileComponentInstallationInstructions';
import ProfileComponentCommunicationPreferences from './components/branding/ProfileComponentCommunicationPreferences';
import ClientSettingsTab from './components/clients/ClientSettingsTab';
import PlanYearList from './components/plans/PlanYearList';
import PlanYearTabsPage from './components/plans/PlanYearTabsPage';
import DataPointsCategoryList from './components/data-points/DataPointsCategoryList';
import DataPointsSubCategoryList from './components/data-points/DataPointsSubCategoryList';
import DataPointCategoryForm from './components/data-points/DataPointCategoryForm';
import DataPointSubCategoryForm from './components/data-points/DataPointSubCategoryForm';
import BenefitPlanList from './components/plans/BenefitPlanList';
import BenefitPlanTabsPage from './components/plans/BenefitPlanTabsPage';
import ViewBenefitsPage from './components/plans/ViewBenefitsPage';
import PlanTypeList from './components/plans/PlanTypeList';
import PlanTypeCategoriesPage from './components/plans/PlanTypeCategoriesPage';
import PlanTypeCategoriesItemPage from './components/plans/PlanTypeCategoriesItemPage';
import PortalTabs from './components/portal-tabs/PortalTabs';
import MyBenefitsList from './components/my-benefits/MyBenefitsList';
import PlanDetailsView from './components/my-benefits/PlanDetailsView';
import { useIndividualHasBenefitAccess } from './hooks/useIndividualHasBenefitAccess';
import { useIndividualViewNavigation } from './hooks/useIndividualViewNavigation';
import BillingClientsPage from './components/billing/BillingClientsPage';
import BillingSMSReportPage from './components/billing/BillingSMSReportPage';
import BillingClientView from './components/billing/BillingClientView';
import VendorList from './components/vendors/VendorList';
import AddEditVendor from './components/vendors/AddEditVendor';
import PlanDetailsList from './components/plan-details/PlanDetailsList';
import AddEditPlanDetails from './components/plan-details/AddEditPlanDetails';
import ArrangePlanTypesPage from './components/plans/ArrangePlanTypesPage';
import MultiTenantImportQueue from './components/fileImports/MultiTenantImportQueue';
import PortalTemplatesList from './components/templates/PortalTemplatesList';
import PortalTemplatePreview from './components/templates/PortalTemplatePreview';
import MultiTenantTemplatesList from './components/templates/MultiTenantTemplatesList';
import MultiTenantNotificationTemplatesList from './components/notifications/templates/MultiTenantNotificationTemplatesList';
import NotificationTemplateTabsPage from './components/notifications/templates/NotificationTemplateTabsPage';
import ViewLivePage from './components/home/ViewLivePage';
import ReportsListPage from './components/reports/ReportsListPage';
import GenerateReportPage from './components/reports/GenerateReportPage';
import ReportsHistoryPage from './components/reports/ReportsHistoryPage';
import AllReportsPage from './components/reports/AllReportsPage';
import ResourceViewerPage from './components/assets/components/ResourceViewerPage';
import PushAssetViewerPage from './components/assets/components/PushAssetViewerPage';
import { ROUTES, ROUTE_BUILDERS } from './constants/routes';
import { readLinkCompanyCode } from './utils/externalAppLinks';
/**
 * Define a list of paths that are excluded from Auth
 * @type {string[]}
 */
export const PUBLIC_PATHS = [
    ROUTES.ABOUT,
    ROUTES.LOGIN,
    ROUTES.FORGOT_PASSWORD,
    ROUTES.FORGOT_EMAIL,
    ROUTES.GET_SUPPORT,
    ROUTES.RESET_PASSWORD_WILDCARD,
    ROUTES.ONBOARDING_WILDCARD,
    ROUTES.VERIFY_MFA_WILDCARD,
    ROUTES.BRANDING,
];

const POST_LOGIN_DEEP_LINK_ROUTES = [
    ROUTES.MY_ASSETS,
    ROUTES.MY_MESSAGES_WITH_OPTIONAL_ID,
    ROUTES.MY_BENEFITS,
    ROUTES.MY_HOME,
    ROUTES.PORTAL_PAGES_VIEW,
    ROUTES.SSO_LINK,
    ROUTES.ASSET_VIEWER,
];

function getPostLoginDeepLink(fromLocation) {
    const pathname = fromLocation?.pathname || '';
    const isDeepLink = POST_LOGIN_DEEP_LINK_ROUTES.some(routePattern =>
        matchPath(routePattern, pathname),
    );

    return isDeepLink ? fromLocation : ROUTES.ROOT;
}

function PortalPageViewRoute({ adminView, canSwitchToIndividualView }) {
    const location = useLocation();

    // The Individual view route guard flips a Client Admin over right after this render.
    if (adminView) {
        return canSwitchToIndividualView ? null : (
            <Navigate to={ROUTES.ROOT} replace />
        );
    }

    const hasNavigationStack =
        Array.isArray(location.state?.navigationStack) &&
        location.state.navigationStack.length > 0;
    if (hasNavigationStack) {
        return <PortalPageView />;
    }

    return <IndividualHomePage />;
}

function LandingPageRoute({
    isLoggedIn,
    multitenantFlag,
    adminView,
    keepCurrentUrl = false,
}) {
    const location = useLocation();

    if (!isLoggedIn) {
        const companyCode =
            readLinkCompanyCode(location.search) ||
            localStorage.getItem('companyCode');

        return (
            <Navigate
                to={ROUTE_BUILDERS.loginWithCompanyCode(companyCode)}
                replace
                state={{
                    from: {
                        pathname: location.pathname,
                        search: location.search,
                        hash: location.hash,
                    },
                }}
            />
        );
    }

    if (multitenantFlag) {
        return <MultiTenantHomePage />;
    }

    if (adminView) {
        return <ClientAdminHomePage />;
    }

    return <IndividualHomePage keepCurrentUrl={keepCurrentUrl} />;
}

function AppRoutes() {
    const location = useLocation();
    const {
        isLoggedIn,
        user,
        multitenantFlag,
        adminView,
        allowAdditionalUser,
        hasSecurityAttribute,
        isSelfRegistrationEnabled,
    } = useAuthContext();
    const showIndividualBenefits = useIndividualHasBenefitAccess();
    const { canSwitchToIndividualView } = useIndividualViewNavigation();
    const shouldRenderPortalPageView =
        Array.isArray(location.state?.navigationStack) &&
        location.state.navigationStack.length > 0;
    const canAccessAdminBenefits = adminView;

    const isLimitedUser = user?.jwt?.type === UserType.LimitedUser;

    return (
        <Routes>
            {isLoggedIn && (
                <>
                    <Route
                        path={ROUTES.GROUPS}
                        element={<PopulationGroupsPage />}
                    />
                    <Route
                        path={ROUTES.GROUPS_NEW}
                        element={<PopulationGroupsPage />}
                    />
                    <Route
                        path={ROUTES.GROUPS_EDIT}
                        element={<PopulationGroupsPage />}
                    />

                    {hasSecurityAttribute([
                        SecurityAttributes.IndividualAccountActions,
                        SecurityAttributes.IndividualFullAccess,
                        SecurityAttributes.IndividualReadOnly,
                    ]) && (
                        <>
                            <Route
                                path={ROUTES.INDIVIDUALS}
                                element={<IndividualSearchPage />}
                            />
                            <Route
                                path={ROUTES.INDIVIDUALS_VIEW}
                                element={<IndividualTabsPage />}
                            />
                        </>
                    )}

                    {hasSecurityAttribute([
                        SecurityAttributes.IndividualFullAccess,
                    ]) && (
                        <Route
                            path={ROUTES.INDIVIDUALS_ADD}
                            element={<CreateIndividualPage />}
                        />
                    )}

                    {hasSecurityAttribute([
                        SecurityAttributes.PortalsFullAccess,
                        SecurityAttributes.PortalsReadOnly,
                    ]) && (
                        <>
                            <Route
                                path={ROUTES.PORTAL_PAGES}
                                element={<PageList />}
                            />
                            <Route
                                path={ROUTES.PORTAL_PAGES_NEW}
                                element={<NewPortalPage />}
                            />
                        </>
                    )}

                    {hasSecurityAttribute([
                        SecurityAttributes.PortalsFullAccess,
                        SecurityAttributes.PortalsReadOnly,
                    ]) && (
                        <>
                            <Route
                                path={ROUTES.VIEW_LIVE}
                                element={<ViewLivePage />}
                            />
                            <Route
                                path={ROUTES.VIEW_LIVE_PORTAL}
                                element={
                                    shouldRenderPortalPageView ? (
                                        <PortalPageView isViewLive={true} />
                                    ) : (
                                        <ViewLivePage />
                                    )
                                }
                            />
                        </>
                    )}

                    {hasSecurityAttribute([
                        SecurityAttributes.TemplatesFullAccess,
                        SecurityAttributes.TemplatesReadOnly,
                        SecurityAttributes.MultiTenantPortalTemplatesFullAccess,
                        SecurityAttributes.MultiTenantPortalTemplatesReadOnly,
                    ]) && (
                        <>
                            <Route
                                path={ROUTES.PORTAL_TEMPLATES}
                                element={<PortalTemplatesList />}
                            />
                            <Route
                                path={ROUTES.PORTAL_TEMPLATE_PREVIEW}
                                element={<PortalTemplatePreview />}
                            />
                            <Route
                                path={ROUTES.PORTAL_TEMPLATES_NEW}
                                element={<NewPortalPage />}
                            />
                        </>
                    )}

                    {hasSecurityAttribute([
                        SecurityAttributes.MultiTenantPortalTemplatesFullAccess,
                        SecurityAttributes.MultiTenantPortalTemplatesReadOnly,
                    ]) && (
                        <Route
                            path={ROUTES.TEMPLATES}
                            element={<MultiTenantTemplatesList />}
                        />
                    )}

                    {hasSecurityAttribute([
                        SecurityAttributes.MultiTenantPortalTemplatesFullAccess,
                    ]) && (
                        <Route
                            path={ROUTES.TEMPLATES_NEW}
                            element={<NewPortalPage />}
                        />
                    )}

                    {hasSecurityAttribute([
                        SecurityAttributes.MultiTenantNotificationTemplatesFullAccess,
                        SecurityAttributes.MultiTenantNotificationTemplatesReadOnly,
                    ]) && (
                        <Route
                            path={ROUTES.NOTIFICATION_TEMPLATES}
                            element={<MultiTenantNotificationTemplatesList />}
                        />
                    )}

                    {hasSecurityAttribute([
                        SecurityAttributes.MultiTenantNotificationTemplatesFullAccess,
                    ]) && (
                        <Route
                            path={ROUTES.NOTIFICATION_TEMPLATES_NEW}
                            element={
                                <NotificationTemplateTabsPage isCreating />
                            }
                        />
                    )}

                    {hasSecurityAttribute([
                        SecurityAttributes.MultiTenantNotificationTemplatesFullAccess,
                        SecurityAttributes.MultiTenantNotificationTemplatesReadOnly,
                    ]) && (
                        <Route
                            path={ROUTES.NOTIFICATION_TEMPLATES_VIEW}
                            element={<NotificationTemplateTabsPage />}
                        />
                    )}

                    {hasSecurityAttribute([
                        SecurityAttributes.AllReportsAccess,
                        SecurityAttributes.ReportsAccess,
                    ]) && (
                        <>
                            <Route
                                path={ROUTES.REPORTS_LIST}
                                element={<ReportsListPage />}
                            />
                            <Route
                                path={ROUTES.REPORTS_GENERATE}
                                element={<GenerateReportPage />}
                            />
                            <Route
                                path={ROUTES.REPORTS_VIEW}
                                element={<GenerateReportPage />}
                            />
                            <Route
                                path={ROUTES.REPORTS_HISTORY}
                                element={<ReportsHistoryPage />}
                            />
                        </>
                    )}

                    {hasSecurityAttribute([
                        SecurityAttributes.AllReportsAccess,
                    ]) && (
                        <>
                            <Route
                                path={ROUTES.REPORTS_ALL}
                                element={<AllReportsPage />}
                            />
                        </>
                    )}

                    {hasSecurityAttribute([
                        SecurityAttributes.OrganizationLevelsFullAccess,
                        SecurityAttributes.OrganizationLevelsReadOnly,
                    ]) && (
                        <>
                            <Route
                                path={ROUTES.ORG_LEVELS}
                                element={<OrgLevelsList />}
                            />
                            <Route
                                path={ROUTES.ORG_LEVELS_EDIT}
                                element={<EditOrgLevel />}
                            />
                        </>
                    )}

                    {hasSecurityAttribute([
                        SecurityAttributes.ImportsFileImport,
                    ]) && (
                        <Route
                            path={ROUTES.NEW_FILE_UPLOAD}
                            element={<NewFileUploadPage />}
                        />
                    )}

                    {hasSecurityAttribute([SecurityAttributes.ImportQueue]) && (
                        <Route
                            path={ROUTES.IMPORT_QUEUE}
                            element={<ImportQueuePage />}
                        />
                    )}

                    {hasSecurityAttribute([
                        SecurityAttributes.CustomImportsFullAccess,
                        SecurityAttributes.CustomImportsReadOnly,
                    ]) && (
                        <Route
                            path={ROUTES.CUSTOM_IMPORTS}
                            element={<ImportTemplatesPage />}
                        />
                    )}

                    {!isSelfRegistrationEnabled &&
                        hasSecurityAttribute([
                            SecurityAttributes.ImportsAutoImport,
                            SecurityAttributes.ImportsAutoImportReadOnly,
                        ]) && (
                            <>
                                <Route
                                    path={ROUTES.AUTO_IMPORTS}
                                    element={<ImportAutomationListPage />}
                                />
                                <Route
                                    path={ROUTES.AUTO_IMPORTS_EDIT}
                                    element={<EditAutoImport />}
                                />
                            </>
                        )}

                    {hasSecurityAttribute([
                        SecurityAttributes.SecurityRolesFullAccess,
                        SecurityAttributes.SecurityRolesReadOnly,
                    ]) && (
                        <>
                            <Route
                                path={ROUTES.SECURITY_ROLES}
                                element={<ListSecurityRoles />}
                            />
                            <Route
                                path={ROUTES.SECURITY_ROLES_VIEW}
                                element={<ListUsersForRole />}
                            />
                        </>
                    )}

                    {hasSecurityAttribute([
                        SecurityAttributes.VendorFullAccess,
                        SecurityAttributes.VendorFullAccessReadOnly,
                    ]) && (
                        <>
                            <Route
                                path={ROUTES.VENDORS}
                                element={<VendorList />}
                            />
                            <Route
                                path={ROUTES.VENDORS_ADD}
                                element={<AddEditVendor />}
                            />
                            <Route
                                path={ROUTES.VENDORS_EDIT}
                                element={<AddEditVendor />}
                            />
                        </>
                    )}

                    {hasSecurityAttribute([
                        SecurityAttributes.PlanDetailFullAccess,
                        SecurityAttributes.PlanDetailReadOnly,
                    ]) && (
                        <>
                            <Route
                                path={ROUTES.PLAN_DETAILS}
                                element={<PlanDetailsList />}
                            />
                        </>
                    )}

                    {hasSecurityAttribute([
                        SecurityAttributes.PlanDetailFullAccess,
                        SecurityAttributes.PlanDetailReadOnly,
                    ]) && (
                        <>
                            <Route
                                path={ROUTES.PLAN_DETAILS_ADD}
                                element={<AddEditPlanDetails />}
                            />
                            <Route
                                path={ROUTES.PLAN_DETAILS_EDIT}
                                element={<AddEditPlanDetails />}
                            />
                        </>
                    )}

                    {hasSecurityAttribute([
                        SecurityAttributes.SecurityRolesFullAccess,
                        SecurityAttributes.SecurityRolesReadOnly,
                    ]) && (
                        <>
                            <Route
                                path={ROUTES.SECURITY_ROLES_EDIT}
                                element={<AddSecurityRole />}
                            />
                            <Route
                                path={ROUTES.SECURITY_ROLES_ADD_USER}
                                element={<AddUserToRole />}
                            />
                            <Route
                                path={ROUTES.SECURITY_ROLES_ADD}
                                element={<AddSecurityRole />}
                            />
                        </>
                    )}

                    {hasSecurityAttribute([
                        SecurityAttributes.ThemeFullAccess,
                        SecurityAttributes.ThemeReadOnly,
                    ]) && (
                        <>
                            <Route
                                path={ROUTES.THEME}
                                element={<ThemePage />}
                            />
                        </>
                    )}

                    {hasSecurityAttribute([
                        SecurityAttributes.ClientSSOReadOnly,
                        SecurityAttributes.ClientSSOFullAccess,
                    ]) && (
                        <>
                            <Route
                                path={ROUTES.SSO_LIST}
                                element={<SSOList />}
                            />
                            <Route
                                path={ROUTES.SSO_CLIENT_VIEW}
                                element={<ClientSSOWizard />}
                            />
                            <Route
                                path={ROUTES.SSO_CLIENT_ADD}
                                element={<ClientSSOWizard />}
                            />
                        </>
                    )}

                    {hasSecurityAttribute([
                        SecurityAttributes.ClientSettingsFullAccess,
                        SecurityAttributes.ClientSettingsReadOnly,
                    ]) && (
                        <>
                            <Route
                                path={ROUTES.SETTINGS}
                                element={<ClientSettingsTab />}
                            />
                        </>
                    )}
                    {/* end of client routes */}

                    {/* multi tenant routes */}
                    {hasSecurityAttribute([
                        SecurityAttributes.AgencyFullAccess,
                        SecurityAttributes.AgencyReadOnly,
                    ]) && (
                        <>
                            <Route
                                path={ROUTES.AGENCIES}
                                element={<AgencyList />}
                            />
                            <Route
                                path={ROUTES.AGENCIES_VIEW}
                                element={<AgencyTabsPage />}
                            />
                        </>
                    )}

                    {hasSecurityAttribute([
                        SecurityAttributes.AdminSecurityRolesFullAccess,
                        SecurityAttributes.AdminSecurityRolesReadOnly,
                    ]) && (
                        <>
                            <Route
                                path={ROUTES.SECURITY_ROLES}
                                element={<ListSecurityRoles />}
                            />
                            <Route
                                path={ROUTES.SECURITY_ROLES_VIEW}
                                element={<ListUsersForRole />}
                            />
                            <Route
                                path={ROUTES.SECURITY_ROLES_EDIT}
                                element={<AddSecurityRole />}
                            />
                        </>
                    )}

                    {hasSecurityAttribute([
                        SecurityAttributes.AdminSecurityRolesFullAccess,
                    ]) && (
                        <>
                            <Route
                                path={ROUTES.SECURITY_ROLES_ADD_USER}
                                element={<AddUserToRole />}
                            />
                            <Route
                                path={ROUTES.SECURITY_ROLES_ADD}
                                element={<AddSecurityRole />}
                            />
                        </>
                    )}

                    {hasSecurityAttribute([
                        SecurityAttributes.UserAccountFullAccess,
                        SecurityAttributes.UserAccountReadOnly,
                    ]) && (
                        <>
                            <Route
                                path={ROUTES.USER_ACCOUNTS}
                                element={<UserAccountList />}
                            />
                            <Route
                                path={ROUTES.USER_ACCOUNTS_VIEW}
                                element={<UserAccountView />}
                            />
                        </>
                    )}

                    {hasSecurityAttribute([
                        SecurityAttributes.UserAccountFullAccess,
                    ]) && (
                        <>
                            <Route
                                path={ROUTES.USER_ACCOUNTS_ADD}
                                element={<UserAccountView />}
                            />
                        </>
                    )}

                    {allowAdditionalUser && (
                        <>
                            <Route
                                path={ROUTES.ADDITIONAL_USERS}
                                element={<AdditionalUsersList />}
                            />
                            <Route
                                path={ROUTES.ADDITIONAL_USERS_ADD}
                                element={<AdditionalUserForm />}
                            />
                            <Route
                                path={ROUTES.ADDITIONAL_USERS_VIEW}
                                element={<AdditionalUserForm />}
                            />
                        </>
                    )}

                    {hasSecurityAttribute([
                        SecurityAttributes.ClientFullAccess,
                        SecurityAttributes.ClientReadOnly,
                    ]) && (
                        <>
                            <Route
                                path={ROUTES.CLIENTS}
                                element={<ClientList />}
                            />
                            <Route
                                path={ROUTES.CLIENTS_VIEW}
                                element={<ClientView />}
                            />
                        </>
                    )}

                    {hasSecurityAttribute([
                        SecurityAttributes.ClientFullAccess,
                    ]) && (
                        <>
                            <Route
                                path={ROUTES.CLIENTS_ADD}
                                element={<AddClientTabsPage />}
                            />
                            <Route
                                path={ROUTES.CLIENTS_ADD_CONTACT}
                                element={<AddContact />}
                            />
                            <Route
                                path={ROUTES.CLIENTS_UPDATE_CONTACT}
                                element={<AddContact />}
                            />
                        </>
                    )}

                    {hasSecurityAttribute([
                        SecurityAttributes.ClientAssignFullAccess,
                        SecurityAttributes.ClientAssignReadOnly,
                    ]) && (
                        <>
                            <Route
                                path={ROUTES.CLIENTS_ADD_USER}
                                element={<AssignUser />}
                            />
                        </>
                    )}

                    {hasSecurityAttribute([
                        SecurityAttributes.GlobalSSOFullAccess,
                        SecurityAttributes.GlobalSSOReadOnly,
                    ]) && (
                        <>
                            <Route
                                path={ROUTES.SSO_LIST}
                                element={<SSOList />}
                            />
                            <Route
                                path={ROUTES.SSO_MULTI_ADD}
                                element={<MultiTenantSSOWizard />}
                            />
                            <Route
                                path={ROUTES.SSO_MULTI_VIEW}
                                element={<MultiTenantSSOWizard />}
                            />
                        </>
                    )}
                    {/* end of multi tenant routes */}

                    {hasSecurityAttribute([
                        SecurityAttributes.BillingFullAccess,
                        SecurityAttributes.BillingReadOnly,
                    ]) && (
                        <>
                            <Route
                                path={ROUTES.BILLING_CLIENTS}
                                element={<BillingClientsPage />}
                            />
                            <Route
                                path={ROUTES.BILLING_CLIENTS_VIEW}
                                element={<BillingClientView />}
                            />
                            <Route
                                path={ROUTES.BILLING_SMS_REPORT}
                                element={<BillingSMSReportPage />}
                            />
                        </>
                    )}

                    <Route path={ROUTES.CHARTS} element={<Charts />} />

                    <Route path={ROUTES.MESSAGES} element={<MessageList />} />
                    <Route
                        path={ROUTES.MESSAGES_TEMPLATES}
                        element={
                            <MultiTenantNotificationTemplatesList
                                isClientView
                            />
                        }
                    />
                    <Route
                        path={ROUTES.MESSAGES_TEMPLATES_VIEW}
                        element={<NotificationTemplateTabsPage isClientView />}
                    />
                    <Route
                        path={ROUTES.MESSAGES_HISTORY}
                        element={<MessagesHistory />}
                    />
                    {hasSecurityAttribute([
                        SecurityAttributes.NotificationFullAccess,
                        SecurityAttributes.NotificationReadOnly,
                    ]) && (
                        <>
                            <Route
                                path={ROUTES.NOTIFICATION_SCHEDULE_LIST}
                                element={<NotificationScheduleList />}
                            />
                            <Route
                                path={ROUTES.NOTIFICATION_SCHEDULE_NEW}
                                element={<NotificationScheduleDetails />}
                            />
                            <Route
                                path={ROUTES.NOTIFICATION_SCHEDULE_HISTORY}
                                element={<NotificationScheduleHistory />}
                            />
                            <Route
                                path={ROUTES.NOTIFICATION_SCHEDULE_VIEW}
                                element={<NotificationScheduleDetails />}
                            />
                        </>
                    )}
                    <Route
                        path={ROUTES.MESSAGES_HISTORY}
                        element={<MessagesHistory />}
                    />
                    <Route
                        path={ROUTES.MESSAGES_HISTORY_DETAILS}
                        element={<MessagesHistoryDetails />}
                    />
                    <Route
                        path={ROUTES.MESSAGES_VIEW}
                        element={<AddMessageTabsPage />}
                    />
                    <Route
                        path={ROUTES.MESSAGES_ADD}
                        element={<AddMessageTabsPage isCreating={true} />}
                    />
                    <Route
                        path={ROUTES.PORTAL_PAGES_VIEW}
                        element={
                            <PortalPageViewRoute
                                adminView={adminView}
                                canSwitchToIndividualView={
                                    canSwitchToIndividualView
                                }
                            />
                        }
                    />

                    <Route
                        path={ROUTES.ASSETS}
                        element={<AssetLibrary viewMode="AssetLibrary" />}
                    />
                    <Route path={ROUTES.ASSETS_NEW} element={<NewAsset />} />
                    <Route
                        path={ROUTES.RESOURCES}
                        element={<AssetLibrary viewMode="ResourceLibrary" />}
                    />
                    <Route
                        path={ROUTES.RESOURCES_NEW}
                        element={<NewAsset viewMode="ResourceLibrary" />}
                    />

                    <Route
                        path={ROUTES.DOCUMENT_VIEWER}
                        element={<DocumentViewer />}
                    />

                    <Route
                        path={ROUTES.SSO_LINK}
                        element={<SSOLinkRedirect />}
                    />

                    <Route
                        path={ROUTES.MY_MESSAGES_WITH_OPTIONAL_ID}
                        element={
                            isLimitedUser ? (
                                <Navigate to={ROUTES.ROOT} replace />
                            ) : (
                                <UserMessageList />
                            )
                        }
                    />

                    {showIndividualBenefits && (
                        <>
                            <Route
                                path={ROUTES.MY_BENEFITS}
                                element={
                                    isLimitedUser &&
                                    !hasSecurityAttribute([
                                        SecurityAttributes.UserAccessBenefits,
                                    ]) ? (
                                        <Navigate to={ROUTES.ROOT} replace />
                                    ) : (
                                        <MyBenefitsList />
                                    )
                                }
                            />
                            <Route
                                path={ROUTES.MY_BENEFITS_PLAN_DETAILS}
                                element={
                                    isLimitedUser &&
                                    !hasSecurityAttribute([
                                        SecurityAttributes.UserAccessBenefits,
                                    ]) ? (
                                        <Navigate to={ROUTES.ROOT} replace />
                                    ) : (
                                        <PlanDetailsView />
                                    )
                                }
                            />
                        </>
                    )}

                    {canAccessAdminBenefits && (
                        <>
                            <Route
                                path={ROUTES.BENEFIT_PLANS}
                                element={<BenefitPlanList />}
                            />
                            <Route
                                path={ROUTES.BENEFIT_PLANS_VIEW}
                                element={<BenefitPlanTabsPage />}
                            />
                            <Route
                                path={ROUTES.BENEFIT_PLANS_ADD}
                                element={
                                    <BenefitPlanTabsPage isCreating={true} />
                                }
                            />
                            <Route
                                path={ROUTES.VIEW_BENEFITS}
                                element={<ViewBenefitsPage />}
                            />
                            {!showIndividualBenefits && (
                                <Route
                                    path={ROUTES.MY_BENEFITS_PLAN_DETAILS}
                                    element={<PlanDetailsView />}
                                />
                            )}
                        </>
                    )}

                    {canAccessAdminBenefits && (
                        <>
                            <Route
                                path={ROUTES.PLAN_YEAR}
                                element={<PlanYearList />}
                            />
                            <Route
                                path={ROUTES.PLAN_YEAR_VIEW}
                                element={<PlanYearTabsPage />}
                            />
                        </>
                    )}

                    {canAccessAdminBenefits && (
                        <>
                            <Route
                                path={ROUTES.PLAN_YEAR_ADD}
                                element={<PlanYearTabsPage isCreating={true} />}
                            />
                        </>
                    )}

                    {canAccessAdminBenefits && (
                        <>
                            <Route
                                path={ROUTES.PLAN_TYPES}
                                element={<PlanTypeList />}
                            />
                            <Route
                                path={ROUTES.PLAN_TYPES_ARRANGE}
                                element={<ArrangePlanTypesPage />}
                            />
                            <Route
                                path={ROUTES.PLAN_TYPE_CATEGORIES}
                                element={<PlanTypeCategoriesPage />}
                            />
                            <Route
                                path={ROUTES.PLAN_TYPE_CATEGORY_ITEM}
                                element={<PlanTypeCategoriesItemPage />}
                            />
                        </>
                    )}

                    {hasSecurityAttribute([
                        SecurityAttributes.MultiImportQueueAccess,
                        SecurityAttributes.MultiImportQueueEnhancedReviewAccess,
                    ]) && (
                        <>
                            <Route
                                path={ROUTES.MULTITENANT_IMPORT_QUEUE}
                                element={<MultiTenantImportQueue />}
                            />
                        </>
                    )}

                    {hasSecurityAttribute([
                        SecurityAttributes.ClientSettingsFullAccess,
                        SecurityAttributes.ClientSettingsReadOnly,
                    ]) && (
                        <>
                            <Route
                                path={ROUTES.DATA_POINTS}
                                element={<DataPointsCategoryList />}
                            />
                            <Route
                                path={ROUTES.DATA_POINTS_NEW}
                                element={<DataPointCategoryForm />}
                            />
                            <Route
                                path={ROUTES.DATA_POINTS_EDIT}
                                element={<DataPointCategoryForm />}
                            />
                            <Route
                                path={ROUTES.DATA_POINTS_SUBCATEGORY}
                                element={<DataPointsSubCategoryList />}
                            />
                            <Route
                                path={ROUTES.DATA_POINTS_SUBCATEGORY_NEW}
                                element={<DataPointSubCategoryForm />}
                            />
                            <Route
                                path={ROUTES.DATA_POINTS_SUBCATEGORY_EDIT}
                                element={<DataPointSubCategoryForm />}
                            />
                        </>
                    )}
                </>
            )}

            <Route path={ROUTES.ACTIONS} element={<PortalActionsList />} />
            <Route
                path={ROUTES.HQ_TRAINING}
                element={<AssetLibrary viewMode="MultiTenantTraining" />}
            />
            <Route
                path={ROUTES.RESOURCE_VIEWER}
                element={<ResourceViewerPage />}
            />
            <Route
                path={ROUTES.ASSET_VIEWER}
                element={
                    isLoggedIn ? (
                        <>
                            <LandingPageRoute
                                isLoggedIn={isLoggedIn}
                                multitenantFlag={multitenantFlag}
                                adminView={adminView}
                                keepCurrentUrl
                            />
                            <PushAssetViewerPage />
                        </>
                    ) : (
                        <Navigate to={ROUTES.ROOT} replace />
                    )
                }
            />
            <Route
                path={ROUTES.ACTIONS_VIEW}
                element={<PortalActionDetail />}
            />

            <Route path={ROUTES.BRANDING} element={<BrandingPage />} />
            <Route path={ROUTES.PROFILE} element={<ProfileComponent />} />
            <Route
                path={ROUTES.USER_INFO}
                element={<ProfileComponentUserInfo />}
            />
            <Route
                path={ROUTES.PASSWORD}
                element={<ProfileComponentPassword />}
            />
            <Route
                path={ROUTES.COMMUNICATION_PREFERENCES}
                element={<ProfileComponentCommunicationPreferences />}
            />
            <Route
                path={ROUTES.INSTALL_APP}
                element={<ProfileComponentInstallationInstructions />}
            />
            {/* <Route path="/faqs" element={<ProfileComponentFAQ />} /> */}
            <Route
                path={ROUTES.PRIVACY_POLICY}
                element={<ProfileComponentPrivacyPolicy />}
            />
            <Route
                path={ROUTES.CONTACT_SUPPORT}
                element={<ProfileComponentContactUs />}
            />

            <Route path={ROUTES.ABOUT} element={<AboutPage />} />
            <Route
                path={ROUTES.LOGIN}
                element={
                    isLoggedIn ? (
                        <Navigate
                            to={getPostLoginDeepLink(location.state?.from)}
                            replace
                        />
                    ) : (
                        <LoginPage />
                    )
                }
            />
            <Route
                path={ROUTES.VERIFY_MFA}
                element={
                    isLoggedIn ? (
                        <Navigate to={ROUTES.ROOT} replace />
                    ) : (
                        <VerifyMFAPage />
                    )
                }
            />
            <Route
                path={ROUTES.FORGOT_PASSWORD}
                element={
                    isLoggedIn ? (
                        <Navigate to={ROUTES.ROOT} replace />
                    ) : (
                        <LoginPage />
                    )
                }
            />
            <Route
                path={ROUTES.FORGOT_EMAIL}
                element={
                    isLoggedIn ? (
                        <Navigate to={ROUTES.ROOT} replace />
                    ) : (
                        <ForgotEmailVerification />
                    )
                }
            />
            <Route
                path={ROUTES.RESET_PASSWORD_USER}
                element={
                    isLoggedIn ? (
                        <Navigate to={ROUTES.ROOT} replace />
                    ) : (
                        <LoginPage />
                    )
                }
            />
            <Route path={ROUTES.ONBOARDING} element={<OnboardingWizard />} />
            <Route
                path={ROUTES.ONBOARDING_SET_USERNAME}
                element={<OnboardingWizard />}
            />
            <Route
                path={ROUTES.ONBOARDING_FIND_USER}
                element={<OnboardingPage />}
            />

            <Route
                path={ROUTES.ROOT}
                element={
                    <LandingPageRoute
                        isLoggedIn={isLoggedIn}
                        multitenantFlag={multitenantFlag}
                        adminView={adminView}
                    />
                }
            />

            <Route
                path={ROUTES.MY_HOME}
                element={<Navigate to={ROUTES.ROOT} replace />}
            />

            {multitenantFlag && (
                <>
                    <Route
                        path={ROUTES.GLOBAL_ASSETS}
                        element={
                            <AssetLibrary viewMode="GlobalAssets"></AssetLibrary>
                        }
                    />
                    <Route
                        path={ROUTES.GLOBAL_ASSETS_NEW}
                        element={<NewAsset />}
                    />
                </>
            )}

            {isLoggedIn && !multitenantFlag && (
                <>
                    <Route path={ROUTES.MY_ASSETS} element={<MyAssets />} />
                    <Route path={ROUTES.MY_ASSETS_NEW} element={<NewAsset />} />
                </>
            )}

            <Route
                path={ROUTES.PORTAL_PAGES_ARRANGE_TABS}
                element={<PortalTabs />}
            />

            <Route path={ROUTES.GET_SUPPORT} element={<GetSupport />} />

            <Route path={ROUTES.NOT_FOUND} element={<NotFoundPage />} />
        </Routes>
    );
}

export default AppRoutes;
